import { Link } from 'react-router-dom';
import { Check, ImageOff } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { resolveImageUrl } from '../lib/imageUrl';
import { formatPrice } from '../lib/formatPrice';
import CartPlusIcon from './CartPlusIcon';
import { toast } from 'sonner';

// Carte produit à plat : ni bordure, ni coin arrondi, ni ombre. L'image occupe
// toute la largeur et garde sa hauteur naturelle — c'est cette hauteur variable
// qui produit le décalage entre les colonnes de la grille.
/**
 * `prioritaire` : la carte fait partie du premier écran.
 *
 * Son image est alors chargée sans attendre. Le chargement différé natif
 * décide de la visibilité au moment de la mise en page ; or la grille
 * répartit les cartes en colonnes flexibles dont la largeur n'est pas encore
 * arrêtée à cet instant. Chrome conclut « hors écran » et ne revient sur sa
 * décision qu'au défilement suivant — d'où des emplacements réservés et vides
 * tant que le visiteur ne touche à rien.
 */
export default function ProductCard({ product, prioritaire = false }) {
  // Adresse publique : slug lisible si disponible, identifiant technique sinon
  const productPath = `/products/${product.slug || product.id}`;
  const { t, getLocalizedField, language } = useLanguage();
  const { addItem } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const imageSrc = resolveImageUrl(product.image_url);
  const showPlaceholder = !imageSrc || imageFailed;
  const nom = getLocalizedField(product, 'name');
  const epuise = product.stock <= 0;
  // Le prix de la fiche vaut celui du format le moins cher (voir
  // declinaisons.py) : on le présente donc comme un point de départ.
  const plusieursFormats = (product.declinaisons || []).length > 1;

  const handleAddToCart = () => {
    setIsAdding(true);
    addItem(product);
    toast.success(t('added'), { description: nom });
    setTimeout(() => setIsAdding(false), 1000);
  };

  return (
    <div className="group bg-white" data-testid={`product-card-${product.id}`}>
      {/* Image — bord à bord, sans marge intérieure */}
      <Link to={productPath} className="block relative overflow-hidden bg-gray-100">
        {showPlaceholder ? (
          // Image absente ou introuvable : visuel neutre plutôt que l'icône
          // d'image cassée du navigateur.
          <div className="w-full aspect-square flex flex-col items-center justify-center gap-2 text-gray-300">
            <ImageOff className="w-8 h-8" />
            <span className="text-xs text-gray-400">
              {language === 'fr' ? 'Photo à venir' : 'Photo coming soon'}
            </span>
          </div>
        ) : (
          <img
            src={imageSrc}
            alt={nom}
            // width/height : avec ces attributs, le navigateur réserve l'espace
            // de l'image avant de la charger. Sans eux, chaque carte n'avait
            // que son texte puis toute la grille se décalait à l'arrivée des
            // photos. h-auto conserve les proportions ; max-h borne les photos
            // très allongées, qui écraseraient sinon toute une colonne.
            width={product.image_width || undefined}
            height={product.image_height || undefined}
            className="block w-full h-auto max-h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
            loading={prioritaire ? 'eager' : 'lazy'}
            fetchpriority={prioritaire ? 'high' : undefined}
            onError={() => setImageFailed(true)}
          />
        )}

        {epuise && (
          <span className="absolute top-0 left-0 px-2 py-1 bg-black/70 text-[11px] font-medium uppercase tracking-wide text-white">
            {language === 'fr' ? 'Rupture' : 'Sold out'}
          </span>
        )}
      </Link>

      {/* Texte */}
      <div className="pt-2 pb-5">
        <Link to={productPath}>
          <h3
            className="text-[13px] leading-snug text-text_secondary line-clamp-2 hover:text-text_primary transition-colors"
            data-testid={`product-name-${product.id}`}
          >
            {nom}
          </h3>
        </Link>

        {/* Conditionnement : savoir si l'on achète une unité ou un carton. */}
        {(product.brand || (!plusieursFormats && product.format)) && (
          <p className="mt-0.5 text-[11px] text-text_secondary/70 line-clamp-1">
            {product.brand}
            {product.brand && !plusieursFormats && product.format && ' · '}
            {!plusieursFormats && product.format}
          </p>
        )}

        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span
            className="text-lg font-bold text-text_primary"
            data-testid={`product-price-${product.id}`}
          >
            {plusieursFormats && (
              <span className="text-xs font-normal text-text_secondary mr-1">
                {language === 'fr' ? 'dès ' : 'from '}
              </span>
            )}
            {formatPrice(product.price, language)}
          </span>

          {/* Le bouton se réduit à son icône : le libellé reste accessible via
              aria-label et title, indispensable pour les lecteurs d'écran. */}
          {plusieursFormats ? (
            <Link
              to={productPath}
              aria-label={language === 'fr' ? 'Choisir le format' : 'Choose a size'}
              title={language === 'fr' ? 'Choisir le format' : 'Choose a size'}
              className="flex-shrink-0 flex items-center justify-center h-10 px-3 border border-gray-300 text-xs font-semibold text-text_primary hover:border-primary hover:bg-primary hover:text-white transition-colors"
              data-testid={`choisir-format-${product.id}`}
            >
              {language === 'fr' ? 'Choisir' : 'Choose'}
            </Link>
          ) : (
          <button
            onClick={handleAddToCart}
            disabled={epuise || isAdding}
            aria-label={epuise ? (language === 'fr' ? 'Produit en rupture' : 'Out of stock') : t('addToCart')}
            title={epuise ? (language === 'fr' ? 'Produit en rupture' : 'Out of stock') : t('addToCart')}
            className={`flex-shrink-0 flex items-center justify-center w-10 h-10 border transition-colors ${
              epuise
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : isAdding
                  ? 'border-green-600 bg-green-600 text-white'
                  : 'border-gray-300 text-text_primary hover:border-primary hover:bg-primary hover:text-white'
            }`}
            data-testid={`add-to-cart-${product.id}`}
          >
            {isAdding ? <Check className="w-4 h-4" /> : <CartPlusIcon className="w-[18px] h-[18px]" />}
          </button>
          )}
        </div>

        {/* Disponibilité - on n'expose pas la quantité exacte au client */}
        {!epuise && product.stock <= 10 && (
          <p className="mt-1 text-[11px] font-medium text-orange-600">
            {language === 'fr' ? 'Derniers exemplaires' : 'Only a few left'}
          </p>
        )}
      </div>
    </div>
  );
}
